import { useRef, useState, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type PinyinApplyResult,
  type PinyinProposalsBundle,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { pinyinMigrationPath } from "../../lib/api-paths.js";
import { ErrorBanner, InfoBanner } from "../../components/ErrorBanner.js";
import { Modal } from "../../components/Modal.js";
import { PrimaryButton, SecondaryButton } from "../../components/FormField.js";
import { Spinner } from "../../components/Spinner.js";

type DialogState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ready"; bundle: PinyinProposalsBundle }
  | { kind: "applying"; bundle: PinyinProposalsBundle }
  | { kind: "applied"; result: PinyinApplyResult }
  | { kind: "error"; message: string };

/**
 * Phase C.2 — 拼音迁移弹窗。
 * 触发 dry-run → review proposals → apply 三步 wizard。
 *
 * 后端：
 *   POST /api/organizations/:orgCode/users/pinyin-migration/dry-run?autoConfirmScore=100
 *   POST /api/organizations/:orgCode/users/pinyin-migration/apply  body=PinyinProposalsBundle
 *
 * 关闭 dialog 不会自动 refetch users — caller 在 onClose 回调里触发。
 */
export function PinyinMigrationDialog({
  orgCode,
  open,
  onClose,
}: {
  orgCode: string;
  open: boolean;
  onClose: (applied: boolean) => void;
}): JSX.Element | null {
  const auth = useAtomValue(authAtom);
  const [state, setState] = useState<DialogState>({ kind: "idle" });
  const [autoConfirm, setAutoConfirm] = useState<number>(100);
  const abortRef = useRef<AbortController | null>(null);

  if (!open) return null;

  const handleDryRun = async (): Promise<void> => {
    if (!auth) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ kind: "running" });
    try {
      const bundle = await apiCall<PinyinProposalsBundle>(
        "POST",
        `${pinyinMigrationPath(orgCode, "dry-run")}?autoConfirmScore=${autoConfirm}`,
        { token: auth.token, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      setState({ kind: "ready", bundle });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError && err.code === "feishu-not-connected") {
        setState({
          kind: "error",
          message: "飞书未连接，请先在「飞书集成」页面填凭据并连接。",
        });
      } else if (err instanceof ApiError && err.code === "feishu-roster-empty") {
        setState({
          kind: "error",
          message:
            "飞书通讯录拉取为空，请检查 DOSIA 飞书应用的通讯录权限（contact 只读 scope）与可见范围是否覆盖全员。",
        });
      } else {
        setState({
          kind: "error",
          message: err instanceof ApiError ? err.message : "Dry-run 失败",
        });
      }
    }
  };

  const handleToggle = (userId: string): void => {
    if (state.kind !== "ready") return;
    setState({
      ...state,
      bundle: {
        ...state.bundle,
        proposals: state.bundle.proposals.map((p) =>
          p.userId === userId ? { ...p, confirmed: !p.confirmed } : p,
        ),
      },
    });
  };

  const handleApply = async (): Promise<void> => {
    if (state.kind !== "ready" || !auth) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ kind: "applying", bundle: state.bundle });
    try {
      const result = await apiCall<PinyinApplyResult>(
        "POST",
        pinyinMigrationPath(orgCode, "apply"),
        { body: state.bundle, token: auth.token, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      setState({ kind: "applied", result });
    } catch (err) {
      if (controller.signal.aborted) return;
      setState({
        kind: "error",
        message: err instanceof ApiError ? err.message : "Apply 失败",
      });
    }
  };

  const isAppliedOrError =
    state.kind === "applied" || state.kind === "error";

  const renderBody = (): ReactNode => {
    switch (state.kind) {
      case "idle":
        return (
          <div className="flex flex-col gap-3">
            <InfoBanner>
              <span className="text-xs">
                将拉取飞书 roster 并跑拼音 / 手机号 / 邮箱匹配，输出建议绑定列表。
                此操作 idempotent，不写 mongo。
              </span>
            </InfoBanner>
            <div className="flex items-center gap-2">
              <label className="text-sm text-dosia-text">
                自动 confirm 分数阈值：
              </label>
              <input
                type="number"
                value={autoConfirm}
                onChange={(e) =>
                  setAutoConfirm(Number.parseInt(e.target.value, 10) || 100)
                }
                className="w-20 rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1 text-sm"
                aria-label="自动 confirm 分数阈值"
              />
              <span className="text-xs text-dosia-text-dim">
                （score &gt;= 该值时自动勾选；100 = mobile/email-exact）
              </span>
            </div>
            <PrimaryButton onClick={() => void handleDryRun()}>
              开始 Dry-run
            </PrimaryButton>
          </div>
        );
      case "running":
        return (
          <div className="flex flex-col items-center gap-2 py-8">
            <Spinner />
            <p className="text-sm text-dosia-text-secondary">
              拉飞书 roster + 跑匹配中（大组织可能 30s+）...
            </p>
          </div>
        );
      case "ready":
        return <ProposalsReview bundle={state.bundle} onToggle={handleToggle} />;
      case "applying":
        return (
          <div className="flex flex-col items-center gap-2 py-8">
            <Spinner />
            <p className="text-sm text-dosia-text-secondary">写入中...</p>
          </div>
        );
      case "applied":
        return (
          <div className="py-4">
            <p className="text-sm text-dosia-text">
              ✓ 写入完成：{state.result.written} 条绑定 /{" "}
              {state.result.skipped} 条跳过
            </p>
          </div>
        );
      case "error":
        return <ErrorBanner>{state.message}</ErrorBanner>;
    }
  };

  const handleClose = (): void => {
    abortRef.current?.abort(); // 取消 in-flight dry-run / apply，防 orphan setState
    const didApply = state.kind === "applied";
    setState({ kind: "idle" });
    onClose(didApply);
  };

  return (
    <Modal
      open
      onClose={handleClose}
      title="拼音匹配飞书 → 批量绑定"
      size="lg"
      footer={
        state.kind === "ready" ? (
          <>
            <SecondaryButton onClick={handleClose}>取消</SecondaryButton>
            <PrimaryButton
              disabled={state.bundle.proposals.every((p) => !p.confirmed)}
              onClick={() => void handleApply()}
            >
              应用所选（
              {state.bundle.proposals.filter((p) => p.confirmed).length} 条）
            </PrimaryButton>
          </>
        ) : isAppliedOrError ? (
          <PrimaryButton onClick={handleClose}>关闭</PrimaryButton>
        ) : (
          <SecondaryButton onClick={handleClose}>关闭</SecondaryButton>
        )
      }
    >
      {renderBody()}
    </Modal>
  );
}

function ProposalsReview({
  bundle,
  onToggle,
}: {
  bundle: PinyinProposalsBundle;
  onToggle: (userId: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-dosia-text-secondary">
        飞书通讯录 {bundle.summary.feishuMirrors} 人 · 未绑定用户{" "}
        {bundle.summary.unboundUsers} · 有候选{" "}
        {bundle.summary.withCandidates} · 无候选{" "}
        {bundle.summary.noCandidates}
      </p>
      <ul className="max-h-[420px] overflow-auto border border-dosia-border rounded-md divide-y divide-dosia-border-light">
        {bundle.proposals.map((p) => {
          const best = p.candidates[0];
          return (
            <li key={p.userId} className="px-3 py-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={p.confirmed}
                  disabled={p.candidates.length === 0}
                  onChange={() => onToggle(p.userId)}
                  aria-label={`确认绑定 ${p.user}`}
                />
                <span className="font-mono text-sm">{p.user}</span>
                {best ? (
                  <>
                    <span className="text-dosia-text-secondary text-xs ml-2">
                      → {best.name ?? best.openId}
                    </span>
                    <span className="rounded-md bg-dosia-accent/15 px-1.5 py-0.5 text-[10px] text-dosia-text">
                      {best.score}
                    </span>
                    <span className="text-xs text-dosia-text-dim">
                      {best.reason}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-amber-700 ml-2">
                    无匹配候选
                  </span>
                )}
              </div>
              {p.candidates.length > 1 ? (
                <p className="text-xs text-dosia-text-dim mt-1 pl-6">
                  其它候选：
                  {p.candidates
                    .slice(1)
                    .map(
                      (c) => `${c.name ?? c.openId}(${c.score})`,
                    )
                    .join(" / ")}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
